// @file Whiteboard-specific AI chat surface strategy.
// Consumed by useAIChatSurfaceStrategy factory — use that composable, not this one directly.
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { useSurfaceAccountsStore } from '@@/pinia/surface_accounts_store'
import type { Suggestion } from '@@/pinia/surface_ai_chat_store'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { computed } from 'vue'
import type { AIChatPersonalizedTool, AIChatSurfaceStrategyReturn } from './useAIChatSurfaceStrategy'

function buildWhiteboardFallbackSuggestions(): Suggestion[] {
  const createdAt = Date.now()
  return [
    {
      short_message: __('What can you help me with here?'),
      long_message: __('What can you help me with on this sandbox?'),
      created_at: createdAt,
    },
    {
      short_message: __('Suggest better padlet settings.'),
      long_message: __('Suggest a better title, description, and wallpaper for this sandbox.'),
      created_at: createdAt,
    },
    {
      short_message: __('What can you change for me?'),
      long_message: __('What wall-level settings can you change for this sandbox?'),
      created_at: createdAt,
    },
  ]
}

function useWhiteboardAIChatSurfaceStrategy(): AIChatSurfaceStrategyReturn {
  const surfaceAccountsStore = useSurfaceAccountsStore()
  const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()

  const surfaceType = 'whiteboard' as const

  const xAiChat = computed(() => {
    if (!isAppUsing('piSandboxChat')) return false
    if (device.app) return false
    if (surfaceContainerSizeStore.isContainerSmallerThanTabletPortrait) return false

    if (isAppUsing('surfaceAiChatSchools') && surfaceAccountsStore.isEducationalLibrary) {
      return surfaceAccountsStore.currentLibrary?.libraryAccess?.canUsePadletIntelligence || false
    }

    if (!surfacePermissionsStore.amIOwner) return false

    if (surfaceAccountsStore.isLibraryTeacherOrAbove || surfaceAccountsStore.isBackpackTeacherOrAbove) {
      return true
    }

    return false
  })

  const introduction = {
    title: __('Padlet Intelligence'),
    description: __('Update your sandbox title, wallpaper, and other settings through our chat interface.'),
    manualMessagePlaceholder: __('Suggest a better title and wallpaper for my sandbox.'),
  }

  const fallbackSuggestions = buildWhiteboardFallbackSuggestions()

  const shouldFetchSuggestionsFromApi = computed<boolean>(() => true)

  const personalizedTools = computed<AIChatPersonalizedTool[]>(() => [])

  return {
    surfaceType,
    xAiChat,
    introduction,
    fallbackSuggestions,
    shouldFetchSuggestionsFromApi,
    personalizedTools,
  }
}

export { useWhiteboardAIChatSurfaceStrategy }
